import React, { useState, useEffect, useCallback } from 'react';
import { HistoryEntry } from '../types/history';
import { HistoryManager } from '../utils/historyManager';
import { TravelExpense, TravelExpenseReport } from '../types/expense';
import './HistoryPanel.css';

interface HistoryPanelProps {
  onRestoreHistory: (expenses: TravelExpense[], reportInfo: Partial<TravelExpenseReport>) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({ onRestoreHistory, isOpen, onClose }) => {
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<HistoryEntry | null>(null);
  const historyManager = HistoryManager.getInstance();

  const loadHistory = useCallback(() => {
    const entries = historyManager.getHistory();
    setHistoryEntries(entries);
  }, [historyManager]);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen, loadHistory]);

  const handleRestore = async (entry: HistoryEntry) => {
    if (window.confirm(`Möchten Sie den Stand vom ${entry.date} wiederherstellen? Die aktuellen Daten werden überschrieben.`)) {
      try {
        await onRestoreHistory(entry.expenses, entry.reportInfo);
        onClose();
      } catch (error) {
        console.error("Failed to restore history:", error);
        // Don't close the panel if restoration failed
      }
    }
  };

  const handleDelete = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('Möchten Sie diesen Verlaufseintrag löschen?')) {
      historyManager.deleteEntry(id);
      loadHistory();
      if (selectedEntry?.id === id) {
        setSelectedEntry(null);
      }
    }
  };

  const handleClearHistory = () => {
    if (window.confirm('Möchten Sie den gesamten Verlauf löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      historyManager.clearHistory();
      loadHistory();
      setSelectedEntry(null);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="history-panel-overlay">
      <div className="history-panel">
        <div className="history-panel-header">
          <h2>
            <i className="fas fa-history"></i> Verlauf
          </h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="history-panel-content">
          {historyEntries.length === 0 ? (
            <div className="no-history">
              <i className="fas fa-clock-rotate-left"></i>
              <p>Noch kein Verlauf vorhanden</p>
              <p className="hint">Der Verlauf wird automatisch erstellt, wenn Sie Ausgaben hinzufügen oder ändern.</p>
            </div>
          ) : (
            <>
              <div className="history-list">
                {historyEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className={`history-entry ${selectedEntry?.id === entry.id ? 'selected' : ''}`}
                    onClick={() => setSelectedEntry(entry)}
                  >
                    <div className="entry-header">
                      <div className="entry-date">{entry.date}</div>
                      <button
                        className="delete-entry-btn"
                        onClick={(e) => handleDelete(entry.id, e)}
                        title="Eintrag löschen"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                    <div className="entry-description">{entry.description}</div>
                    <div className="entry-stats">
                      <span className="stat">
                        <i className="fas fa-file-invoice"></i> {entry.expenseCount} Ausgaben
                      </span>
                      <span className="stat">
                        <i className="fas fa-euro-sign"></i> {formatAmount(entry.totalAmount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {selectedEntry && (
                <div className="history-detail">
                  <h3>Details</h3>
                  <div className="detail-info">
                    <div className="info-row">
                      <span className="label">Zeitpunkt:</span>
                      <span className="value">{selectedEntry.date}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Anzahl Ausgaben:</span>
                      <span className="value">{selectedEntry.expenseCount}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Gesamtbetrag:</span>
                      <span className="value">{formatAmount(selectedEntry.totalAmount)}</span>
                    </div>
                    {selectedEntry.reportInfo.employeeName && (
                      <div className="info-row">
                        <span className="label">Mitarbeiter:</span>
                        <span className="value">{selectedEntry.reportInfo.employeeName}</span>
                      </div>
                    )}
                    {selectedEntry.reportInfo.quarterPeriod && (
                      <div className="info-row">
                        <span className="label">Zeitraum:</span>
                        <span className="value">{selectedEntry.reportInfo.quarterPeriod}</span>
                      </div>
                    )}
                  </div>

                  <div className="expense-preview">
                    <h4>Ausgaben in diesem Stand:</h4>
                    <div className="preview-list">
                      {selectedEntry.expenses.slice(0, 5).map((expense, index) => (
                        <div key={index} className="preview-item">
                          <span className="preview-date">{expense.date}</span>
                          <span className="preview-desc">{expense.description}</span>
                          <span className="preview-amount">{formatAmount(expense.amount)}</span>
                        </div>
                      ))}
                      {selectedEntry.expenses.length > 5 && (
                        <div className="preview-more">
                          ... und {selectedEntry.expenses.length - 5} weitere Ausgaben
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    className="restore-btn"
                    onClick={() => handleRestore(selectedEntry)}
                  >
                    <i className="fas fa-undo"></i> Diesen Stand wiederherstellen
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {historyEntries.length > 0 && (
          <div className="history-panel-footer">
            <button className="clear-history-btn" onClick={handleClearHistory}>
              <i className="fas fa-trash-alt"></i> Verlauf löschen
            </button>
            <div className="history-info">
              {historyEntries.length} Einträge im Verlauf
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPanel;
